// @flow
import React from 'react';
import { get, sumBy, size } from 'lodash';
import { AppMobileHeader, ReportPage, ReportsMenu, PlatformSwitch, EmptyState, ConditionalFeature, BlockedReport, AppMobileFooter } from '@blocks';
// import { AppMobileHeader, ReportsMenu, PlatformSwitch, EmptyState } from '@blocks';
import { Loading, FormattedText, Table, Button } from '@elements';
import type { ReportsDebitData } from '@types';
import { withTranslation } from 'react-i18next';
import { createNsTranslator } from '@helpers/helpers';
import FinancialItemCard, { FinancialItemTotal } from '../../../blocks/PatientFinancial/FinancialItemCard';
import './ReportsDebt.scss';

type Props = {
  data: ReportsDebitData,
  isLoading: boolean,
  onItemClick: Function,
  onPageChange: Function,
  // onRefresh: Function,
  isFeatureEnabled: boolean,
  t: Function,
  singleDoctor: boolean,
  isReportBlocked: boolean,
}

class ReportsDebt extends React.Component<Props> {
  render() {
    const {
      isLoading, data, isFeatureEnabled, singleDoctor, isReportBlocked,
    } = this.props;

    const _ = createNsTranslator('reports', this.props.t);

    if (isReportBlocked) {
      return (
        <BlockedReport
          backLinkTo="/relatorios"
          title={_('debt/debtor_patients')}
        />
      );
    }

    const indebtPatients = get(data, 'patients', []);
    const indebtPayments = get(data, 'payments', []);

    // only blank the screen while there is nothing to show yet; reloads and
    // page changes keep the current data mounted (tables show their own spinner)
    const isFirstLoad = isLoading && !data;

    const page = parseInt(get(data, ['meta', 'page'], 1), 10);
    const totalPages = parseInt(get(data, ['meta', 'totalPages'], 1), 10);
    const totalPatients = parseInt(get(data, ['meta', 'totalPatients'], size(indebtPatients)), 10);

    // the API total covers all pages; the client-side sum only covers the current page
    const patientsTotal = get(data, ['meta', 'totalBalance'], sumBy(indebtPatients, item => parseFloat(item.financialBalance)));

    const renderEmptyState = () => (
      <EmptyState
        title={_('debt/empty_title')}
        hint={_('debt/empty_hint')}
      />
    );


    const isPaymentsItensEmpty = size(indebtPayments) === 0;
    const isPatientsItensEmpty = size(indebtPatients) === 0;
    const isAllTablesEmpty = isPaymentsItensEmpty && isPatientsItensEmpty;

    const paginationControls = () => (totalPages > 1 || page > 1) && (
      <div className="paginationControls">
        <Button disabled={isLoading || page <= 1} onClick={this.handlePreviousPage}>
          {_('debt/previous_page')}
        </Button>
        <span className="pageIndicator">
          {String(_('debt/page_of')).replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
        </span>
        <Button disabled={isLoading || page >= totalPages} onClick={this.handleNextPage}>
          {_('debt/next_page')}
        </Button>
      </div>
    );

    const indebtTable = () => (
      <Table
        itemKey="id"
        columns={[
          {
            dataIndex: 'date',
            content: _('label/date', 'global'),
            onRenderItem: (item) => <FormattedText type="date" text={item.date} />,
          },
          {
            dataIndex: 'method',
            content: _('label/payment_method_short', 'global'),
            onRenderItem: (item) => <FormattedText type="paymentMethod" text={item.method} />,
          },
          {
            dataIndex: 'name',
            content: _('label/value', 'global'),
            rowClass: 'negative',
            onRenderItem: (item) => <FormattedText type="currency" text={item.price} />,
          },
          {
            dataIndex: 'patientName',
            content: _('label/patient', 'global'),
          },
          {
            dataIndex: 'doctorName',
            content: _('label/doctor', 'global'),
            hidden: singleDoctor,
          },
          {
            dataIndex: 'id',
            content: <React.Fragment>&nbsp;</React.Fragment>,
            className: 'actionCell',
            desktopOnly: true,
            onRenderItem: () => <span className="linkAction">{_('action/view_patient', 'global')}</span>,
          },
        ]}
        fullTable
        isLoading={isLoading}
        items={indebtPayments}
        onItemClick={this.onItemClick}
        emptyMessage={_('services/empty_title')}
        mobileClickLabel={_('action/view_patient', 'global')}
      />
    );


    const reportTable = () => (
      <Table
        itemKey="id"
        columns={[
          {
            dataIndex: 'indebtedAt',
            content: _('label/last_change', 'global'),
            onRenderItem: (item) => <FormattedText type="date" text={item.indebtedAt} />,
          },
          {
            dataIndex: 'financialBalance',
            content: _('label/balance', 'global'),
            onRenderItem: (item) => <FormattedText type="currency" text={item.financialBalance} options={{ allowColor: true }} />,
          },
          {
            dataIndex: 'name',
            content: _('label/patient', 'global'),
            rowClass: 'negative',
          },
          {
            dataIndex: 'id',
            content: <React.Fragment>&nbsp;</React.Fragment>,
            className: 'actionCell',
            desktopOnly: true,
            onRenderItem: () => <span className="linkAction">{_('action/view_patient', 'global')}</span>,
          },
        ]}
        fullTable
        isLoading={isLoading}
        items={indebtPatients}
        onItemClick={this.onItemClick}
        mobileClickLabel={_('action/view_patient', 'global')}
        // In the app, the same compact card as the patient's financial lists: the whole card opens the patient
        renderMobileItem={(patient, { onClick }) => (
          <FinancialItemCard
            title={patient.name}
            value={<FormattedText type="currency" text={patient.financialBalance} options={{ allowColor: true }} />}
            date={patient.indebtedAt}
            dateLabel={_('label/last_change', 'global')}
            onClick={onClick}
          />
        )}
        renderFooter={(isMobile: boolean) => (
          <React.Fragment>
            {!isMobile && (
              <tr>
                <td>
                  {_('label/total', 'global')}
                </td>
                <td><FormattedText type="currency" text={patientsTotal} options={{ allowColor: true }} /></td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            )}
            {isMobile && (
              <FinancialItemTotal label={_('label/total', 'global')} value={<FormattedText type="currency" text={patientsTotal} options={{ allowColor: true }} />} />
            )}
          </React.Fragment>
        )}
      />
    );


    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <div id="reportsDebtPage" className="appContent reportsPage">
            <ReportsMenu />
            <section className="mainSection">
              <ConditionalFeature enabled={isFeatureEnabled} transparent>
                <div className="centerContent">
                  <div className="toolbar">
                    <h1>{_('debt/debtor_patients')}</h1>
                  </div>
                  {isFirstLoad &&
                    <Loading />
                  }
                  {!isFirstLoad && isAllTablesEmpty &&
                    <React.Fragment>
                      {renderEmptyState()}
                      {paginationControls()}
                    </React.Fragment>
                  }
                  {!isAllTablesEmpty &&
                    <div className="desktopCard">
                      {!isPaymentsItensEmpty &&
                        <section>
                          <div className="toolbar">
                            <h2>{_('debt/debit_playments')}</h2>
                          </div>
                          {indebtTable()}
                        </section>
                      }

                      {!isPatientsItensEmpty &&
                        <section>
                          <div className="toolbar">
                            <h2>{_('debt/patients_with_negative_balance')}</h2>
                            <span className="totalPatients">
                              {String(_('debt/total_debtor_patients')).replace('{total}', String(totalPatients))}
                            </span>
                          </div>
                          {reportTable()}
                          {paginationControls()}
                        </section>
                      }
                    </div>
                  }
                </div>
              </ConditionalFeature>
            </section>
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            <AppMobileHeader title={_('debt/debtor_patients')} />
            <ReportPage
              id="reportsDebtPage"
              isLoading={isFirstLoad}
              isFeatureEnabled={isFeatureEnabled}
              emptyState={() => isAllTablesEmpty && (
                <React.Fragment>
                  {renderEmptyState()}
                  {paginationControls()}
                </React.Fragment>
              )}
              noPadding
            >
              <ReportPage.Table title={_('debt/patients_with_negative_balance')}>
                {reportTable()}
                {paginationControls()}
              </ReportPage.Table>
            </ReportPage>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }

  onItemClick = (item: Object) => this.props.onItemClick(item.patientId || item.id);

  getPage = () => parseInt(get(this.props.data, ['meta', 'page'], 1), 10);

  handlePreviousPage = () => this.props.onPageChange(this.getPage() - 1);

  handleNextPage = () => this.props.onPageChange(this.getPage() + 1);
}

export default withTranslation()(ReportsDebt);
